import type {
  AdminParticipantListItem,
  AdminSummary,
  InventoryContent,
  JourneyAnswer,
  Participant,
  ParticipantSession,
} from "./types";

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`/api${path}`, {
    credentials: "same-origin",
    ...options,
    headers: {
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...options.headers,
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error || "The request could not be completed.");
  }
  return (await response.json()) as T;
}

function participantHeaders(token: string): HeadersInit {
  return { "X-Participant-Token": token };
}

export const api = {
  content: () => request<InventoryContent>("/content"),
  start: (input: Record<string, unknown>) =>
    request<ParticipantSession>("/participants", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  resume: (resumeToken: string, email: string) =>
    request<ParticipantSession>("/resume", {
      method: "POST",
      body: JSON.stringify({ resumeToken, email }),
    }),
  participant: (id: string, token: string) =>
    request<Participant>(`/participants/${id}`, {
      headers: participantHeaders(token),
    }),
  stage: (id: string, token: string, stage: string, currentStep = 0) =>
    request<Participant>(`/participants/${id}/stage`, {
      method: "PATCH",
      headers: participantHeaders(token),
      body: JSON.stringify({ stage, currentStep }),
    }),
  inventory: (id: string, token: string, question: number, letter: string) =>
    request<Participant>(`/participants/${id}/inventory/${question}`, {
      method: "PUT",
      headers: participantHeaders(token),
      body: JSON.stringify({ letter }),
    }),
  journey: (
    id: string,
    token: string,
    question: number,
    answer: JourneyAnswer,
  ) =>
    request<Participant>(`/participants/${id}/journey/${question}`, {
      method: "PUT",
      headers: participantHeaders(token),
      body: JSON.stringify(answer),
    }),
  complete: (id: string, token: string) =>
    request<{ participant: Participant; emailStatus: string }>(
      `/participants/${id}/complete`,
      {
        method: "POST",
        headers: participantHeaders(token),
      },
    ),
  result: (token: string) => request<Participant>(`/results/${token}`),
  emailResult: (token: string) =>
    request<{ participant: Participant; emailStatus: string }>(
      `/results/${token}/email`,
      { method: "POST" },
    ),
  admin: {
    session: () => request<{ ok: boolean }>("/admin/session"),
    login: (username: string, password: string) =>
      request<{ ok: boolean }>("/admin/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      }),
    logout: () => request<{ ok: boolean }>("/admin/logout", { method: "POST" }),
    summary: () => request<AdminSummary>("/admin/summary"),
    participants: (query: string) =>
      request<AdminParticipantListItem[]>(`/admin/participants${query}`),
    archivedParticipants: () =>
      request<AdminParticipantListItem[]>("/admin/archived-participants"),
    participant: (id: string) => request<Participant>(`/admin/participants/${id}`),
    resumeLink: (id: string) =>
      request<{ url: string }>(`/admin/participants/${id}/resume-link`, { method: "POST" }),
    archiveParticipant: (id: string) =>
      request<Participant>(`/admin/participants/${id}/archive`, { method: "POST" }),
    unarchiveParticipant: (id: string) =>
      request<Participant>(`/admin/participants/${id}/unarchive`, { method: "POST" }),
  },
};
